import { Fragment } from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const delta = 2;
    const range: number[] = [];
    const rangeWithDots: (number | '...')[] = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const baseBtn =
    'h-8 min-w-[32px] px-2 text-sm font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand-text)] focus:ring-offset-1 disabled:opacity-40 disabled:cursor-not-allowed';
  const inactiveBtn = `${baseBtn} bg-[var(--surface)] border border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--surface-2)] hover:border-[var(--border-strong)]`;
  const activeBtn = `${baseBtn} border text-white`;

  return (
    <nav className="flex items-center justify-center gap-1" aria-label="Paginação">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={inactiveBtn}
        aria-label="Página anterior"
      >
        ← Anterior
      </button>

      {getVisiblePages().map((page, index) => (
        <Fragment key={index}>
          {page === '...' ? (
            <span className="h-8 flex items-center px-1 text-sm text-[var(--text-faint)]">…</span>
          ) : (
            <button
              onClick={() => onPageChange(page)}
              className={currentPage === page ? activeBtn : inactiveBtn}
              style={currentPage === page ? { backgroundColor: 'var(--brand-text)', borderColor: 'var(--brand-text)' } : undefined}
              aria-label={`Ir para página ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          )}
        </Fragment>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={inactiveBtn}
        aria-label="Próxima página"
      >
        Próxima →
      </button>
    </nav>
  );
}
